import {
  ISavedFilterSets,
  ISubmissionWorkflowState,
  ITags
} from '@publishing-engine/domain/models';
import { IDashboardSubmissionConfigResponse } from '@publishing-engine/domain/types';
import { SUBMISSION_STATUS_MAP } from 'src/constants';
import { formatCriterionValue } from 'src/utils/submissionsList';

type FocusIssuesFilter = { id: string; name: string };
export function getMatchedNames(
  availableFilters: IDashboardSubmissionConfigResponse['filters'],
  saveFilters: ISavedFilterSets,
  manageTags: ITags[],
  advancedSearchFields?: IDashboardSubmissionConfigResponse['advancedSearchFields']
): string {
  const matchedNames: string[] = [];
  const advancedSearchLabels = new Map(
    (advancedSearchFields ?? []).map(field => [field.value, field.label])
  );

  for (const key in saveFilters) {
    switch (key) {
      case 'searchParams':
        matchedNames.push(saveFilters[key]);
        break;
      case 'workflowPhase':
        saveFilters[key].forEach((phase: string) => {
          const typedPhase = phase as ISubmissionWorkflowState; // Explicitly cast to ISubmissionWorkflowState
          if (SUBMISSION_STATUS_MAP[typedPhase]) {
            matchedNames.push(SUBMISSION_STATUS_MAP[typedPhase].label);
          }
        });
        break;
      case 'journalId':
        saveFilters[key].forEach((id: string) => {
          const journalFilter = availableFilters.find(
            (filter: any) => filter.key === 'journals'
          );
          const journal =
            journalFilter &&
            'items' in journalFilter &&
            Array.isArray((journalFilter as any).items)
              ? (journalFilter as any).items.find((j: any) => j.id === id)
              : undefined;
          if (journal) matchedNames.push(journal.data?.name ?? journal.name);
        });
        break;
      case 'tagId':
        saveFilters[key].forEach((id: string) => {
          const tag = manageTags.find((j: any) => j.id === id);
          if (tag) matchedNames.push(tag.name);
        });
        break;
      case 'documentType':
        saveFilters[key].forEach((id: string) => {
          const documentTypeFilter = availableFilters.find(
            (filter: any) => filter.key === 'documentTypes'
          );
          const docType =
            documentTypeFilter &&
            'items' in documentTypeFilter &&
            Array.isArray((documentTypeFilter as any).items)
              ? (documentTypeFilter as any).items.find((d: any) => d.id === id)
              : undefined;
          if (docType) matchedNames.push(docType.name);
        });
        break;
      case 'assigneeId':
        saveFilters[key].forEach((id: string) => {
          const editorialAssigneesFilter = availableFilters.find(
            (filter: any) => filter.key === 'editorialAssignees'
          );
          const assignee =
            editorialAssigneesFilter &&
            'items' in editorialAssigneesFilter &&
            Array.isArray((editorialAssigneesFilter as any).items)
              ? (editorialAssigneesFilter as any).items.find(
                  (a: any) => a.userId === id
                )
              : undefined;
          if (assignee) matchedNames.push(assignee.label);
        });
        break;
      case 'focusIssueId':
        saveFilters[key].forEach((id: string) => {
          const focusIssuesFilter = availableFilters.find(
            (filter: any) => filter.key === 'focusIssues'
          );
          const focusIssue =
            focusIssuesFilter &&
            'items' in focusIssuesFilter &&
            Array.isArray((focusIssuesFilter as any).items)
              ? (focusIssuesFilter as any).items.find(
                  (a: FocusIssuesFilter) => a.id === id
                )
              : undefined;
          if (focusIssue) matchedNames.push(focusIssue.name);
        });
        break;
      case 'advancedSearch':
        // Criteria are stored as field names; fall back to the raw name for a
        // field the current config no longer advertises. A multi-value
        // criterion lists its values, and an empty array counts as no value.
        (saveFilters[key] ?? []).forEach(criterion => {
          const formatted = formatCriterionValue(criterion.value);
          if (!formatted) return;
          const label =
            advancedSearchLabels.get(criterion.field) ?? criterion.field;
          matchedNames.push(`${label}: ${formatted}`);
        });
        break;
    }
  }

  return matchedNames.join(', ');
}
